import React, { useState, useEffect } from "react";
import { View, TextInput, ScrollView, Pressable, StyleSheet } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import Chip from "@/components/ui/Chip";
import useDateTime from "@/hooks/useDateTime";
import { supabase } from "@/lib/supabase-client";

// Note type definition
export type Note = {
  id: string;
  author_id: string;
  author_name: string;
  message: string;
  created_at: string;
  position_ids?: string[];
};

// Position type for lists.positions
type Position = {
  id: string;
  name: string;
  description: string | null;
};

export type NoteEditorProps = {
  notes: Note[];
  inheritedNotes?: Note[];
  onSave: (notes: Note[]) => void;
  onCancel?: () => void;
  currentUserId: string;
  currentUserName: string;
};

// Generate UUID
function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function NoteEditor({
  notes,
  inheritedNotes = [],
  onSave,
  onCancel,
  currentUserId,
  currentUserName,
}: NoteEditorProps) {
  const { colors } = useTheme();
  const { formatTimestamp } = useDateTime();
  const [localNotes, setLocalNotes] = useState<Note[]>(notes);
  const [newNoteText, setNewNoteText] = useState("");
  const [positions, setPositions] = useState<Position[]>([]);
  const [selectedPositionIds, setSelectedPositionIds] = useState<string[]>([]);
  const [positionsLoading, setPositionsLoading] = useState(true);
  const [showPositions, setShowPositions] = useState(false);

  // Fetch positions from lists.positions
  useEffect(() => {
    const fetchPositions = async () => {
      try {
        const { data } = await supabase
          .schema("lists")
          .from("positions")
          .select("id, name, description")
          .order("name", { ascending: true });
        setPositions(data || []);
      } catch (error) {
        console.error("Failed to fetch positions:", error);
      } finally {
        setPositionsLoading(false);
      }
    };
    fetchPositions();
  }, []);

  const togglePosition = (positionId: string) => {
    setSelectedPositionIds((prev) =>
      prev.includes(positionId) ? prev.filter((id) => id !== positionId) : [...prev, positionId],
    );
  };

  const handleSelectAllPositions = () => {
    if (selectedPositionIds.length === positions.length) {
      // All selected, so deselect all
      setSelectedPositionIds([]);
    } else {
      // Select all
      setSelectedPositionIds(positions.map((p) => p.id));
    }
  };

  const allPositionsSelected = positions.length > 0 && selectedPositionIds.length === positions.length;
  const somePositionsSelected = selectedPositionIds.length > 0 && selectedPositionIds.length < positions.length;

  const handleAddNote = () => {
    if (!newNoteText.trim()) return;

    const newNote: Note = {
      id: generateUUID(),
      author_id: currentUserId,
      author_name: currentUserName,
      message: newNoteText.trim(),
      created_at: new Date().toISOString(),
      position_ids: selectedPositionIds.length > 0 ? selectedPositionIds : undefined,
    };

    setLocalNotes([newNote, ...localNotes]);
    setNewNoteText("");
    setSelectedPositionIds([]);
  };

  const handleRemoveNote = (noteId: string) => {
    setLocalNotes(localNotes.filter((n) => n.id !== noteId));
  };

  const handleConfirm = () => {
    onSave(localNotes);
  };

  // Format relative time
  const getRelativeTime = (timestamp: string) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return formatTimestamp(timestamp, false);
  };

  const styles = StyleSheet.create({
    container: {
      padding: 12,
      gap: 12,
      minWidth: 380,
    },
    addSection: {
      gap: 10,
    },
    textInput: {
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      paddingHorizontal: 12,
      paddingVertical: 10,
      fontSize: 14,
      color: colors.text,
      backgroundColor: colors.background,
      minHeight: 100,
      textAlignVertical: "top",
    },
    addButton: {
      alignSelf: "flex-end",
      height: 32,
      paddingHorizontal: 16,
    },
    notesSection: {
      gap: 8,
    },
    sectionHeader: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
    },
    scrollView: {
      maxHeight: 350,
    },
    noteItem: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: 10,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.border,
      backgroundColor: colors.backgroundSecondary,
      borderRadius: 8,
      marginBottom: 6,
    },
    noteItemInherited: {
      opacity: 0.8,
    },
    noteContent: {
      flex: 1,
      gap: 4,
    },
    noteHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
    },
    noteAuthor: {
      fontSize: 13,
      fontWeight: "600",
    },
    noteTime: {
      fontSize: 11,
      color: colors.textSecondary,
    },
    noteMessage: {
      fontSize: 14,
      lineHeight: 20,
    },
    removeButton: {
      padding: 4,
    },
    inheritedBadge: {
      paddingHorizontal: 8,
      paddingVertical: 2,
      backgroundColor: colors.cyan?.background,
    },
    emptyState: {
      padding: 20,
      alignItems: "center",
    },
    emptyText: {
      color: colors.textSecondary,
      textAlign: "center",
    },
    footer: {
      marginTop: 8,
      gap: 8,
    },
    noteCount: {
      opacity: 0.7,
    },
    footerButtons: {
      flexDirection: "row",
      gap: 8,
      justifyContent: "flex-end",
    },
    cancelButton: {
      height: 32,
      paddingHorizontal: 16,
      backgroundColor: "transparent",
      borderWidth: 1,
    },
    confirmButton: {
      height: 32,
      paddingHorizontal: 16,
    },
    // Position selection styles
    positionSection: {
      gap: 8,
      marginTop: 4,
    },
    positionToggleHeader: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingVertical: 10,
      paddingHorizontal: 12,
      backgroundColor: colors.backgroundSecondary,
      borderRadius: 8,
    },
    positionToggleContent: {
      flexDirection: "row",
      alignItems: "center",
      gap: 8,
      flex: 1,
    },
    positionLabel: {
      fontSize: 14,
      fontWeight: "500",
    },
    positionCount: {
      fontSize: 12,
      color: colors.textSecondary,
    },
    chipContainer: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 6,
    },
    chip: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      paddingVertical: 4,
      paddingLeft: 10,
      paddingRight: 6,
      backgroundColor: colors.primary,
      borderRadius: 16,
    },
    allPositionsChip: {
      flexDirection: "row",
      alignItems: "center",
      gap: 4,
      paddingVertical: 4,
      paddingLeft: 10,
      paddingRight: 6,
      backgroundColor: colors.primary,
      borderRadius: 16,
    },
    chipText: {
      fontSize: 12,
      color: "white",
    },
    positionList: {
      maxHeight: 180,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      backgroundColor: colors.background,
    },
    positionItem: {
      flexDirection: "row",
      alignItems: "center",
      gap: 10,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: colors.border,
    },
    positionItemSelected: {
      backgroundColor: colors.backgroundHover,
    },
    positionName: {
      fontSize: 14,
      flex: 1,
    },
    notePositions: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 4,
      marginTop: 6,
    },
    positionBadge: {
      paddingHorizontal: 8,
      paddingVertical: 2,
      backgroundColor: colors.backgroundSecondary,
    },
    loadingText: {
      padding: 12,
      color: colors.textSecondary,
      textAlign: "center",
      fontSize: 13,
    },
    emptyPositionText: {
      padding: 12,
      color: colors.textSecondary,
      textAlign: "center",
      fontSize: 13,
    },
  });

  const totalNotes = localNotes.length + inheritedNotes.length;

  return (
    <View style={styles.container}>
      {/* Title */}
      <ThemedText type="subtitle">Notes</ThemedText>

      {/* Add Note Section */}
      <View style={styles.addSection}>
        <TextInput
          style={styles.textInput}
          placeholder="Add a note..."
          placeholderTextColor={colors.textSecondary}
          value={newNoteText}
          onChangeText={setNewNoteText}
          multiline
          maxLength={500}
        />

        {/* Position Selection */}
        <View style={styles.positionSection}>
          {/* Selected Positions as Chips - Always visible */}
          {selectedPositionIds.length > 0 && (
            <View style={styles.chipContainer}>
              {allPositionsSelected ? (
                <Pressable style={styles.allPositionsChip} onPress={handleSelectAllPositions}>
                  <ThemedText style={styles.chipText}>All Positions</ThemedText>
                  <MaterialCommunityIcons name="close" size={14} color="white" />
                </Pressable>
              ) : (
                selectedPositionIds.map((posId) => {
                  const pos = positions.find((p) => p.id === posId);
                  return (
                    <Pressable key={posId} style={styles.chip} onPress={() => togglePosition(posId)}>
                      <ThemedText style={styles.chipText}>{pos?.name || posId}</ThemedText>
                      <MaterialCommunityIcons name="close" size={14} color="white" />
                    </Pressable>
                  );
                })
              )}
            </View>
          )}

          {/* Toggle Header */}
          <Pressable style={styles.positionToggleHeader} onPress={() => setShowPositions(!showPositions)}>
            <View style={styles.positionToggleContent}>
              <MaterialCommunityIcons
                name={showPositions ? "chevron-down" : "chevron-right"}
                size={20}
                color={colors.text}
              />
              <Pressable
                onPress={(e) => {
                  e.stopPropagation();
                  handleSelectAllPositions();
                }}
                style={{ marginRight: 4 }}
              >
                <MaterialCommunityIcons
                  name={
                    allPositionsSelected
                      ? "checkbox-marked"
                      : somePositionsSelected
                        ? "minus-box"
                        : "checkbox-blank-outline"
                  }
                  size={22}
                  color={allPositionsSelected || somePositionsSelected ? colors.primary : colors.text}
                />
              </Pressable>
              <ThemedText style={styles.positionLabel}>Positions</ThemedText>
              <ThemedText style={styles.positionCount}>
                ({positions.length} available
                {selectedPositionIds.length > 0 ? `, ${selectedPositionIds.length} selected` : ""})
              </ThemedText>
            </View>
          </Pressable>

          {/* Position List with Checkboxes - Collapsible */}
          {showPositions && (
            <ScrollView style={styles.positionList} nestedScrollEnabled>
              {positionsLoading ? (
                <ThemedText style={styles.loadingText}>Loading positions...</ThemedText>
              ) : positions.length === 0 ? (
                <ThemedText style={styles.emptyPositionText}>No positions available</ThemedText>
              ) : (
                positions.map((position) => {
                  const isSelected = selectedPositionIds.includes(position.id);
                  return (
                    <Pressable
                      key={position.id}
                      style={[styles.positionItem, isSelected && styles.positionItemSelected]}
                      onPress={() => togglePosition(position.id)}
                    >
                      <MaterialCommunityIcons
                        name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
                        size={22}
                        color={isSelected ? colors.primary : colors.text}
                      />
                      <ThemedText style={styles.positionName}>{position.name}</ThemedText>
                    </Pressable>
                  );
                })
              )}
            </ScrollView>
          )}
        </View>

        <Button
          text="Add Note"
          icon="plus"
          iconPosition="left"
          style={styles.addButton}
          onPress={handleAddNote}
          enabled={newNoteText.trim().length > 0}
        />
      </View>

      {/* Notes List */}
      <View style={styles.notesSection}>
        <View style={styles.sectionHeader}>
          <ThemedText type="defaultSemiBold">All Notes ({totalNotes})</ThemedText>
        </View>

        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator>
          {totalNotes === 0 ? (
            <View style={styles.emptyState}>
              <MaterialCommunityIcons name="note-text-outline" size={32} color={colors.textSecondary} />
              <ThemedText style={styles.emptyText}>No notes yet</ThemedText>
            </View>
          ) : (
            <>
              {/* Own entity notes (editable) */}
              {localNotes.map((note) => (
                <View key={note.id} style={styles.noteItem}>
                  <View style={styles.noteContent}>
                    <View style={styles.noteHeader}>
                      <ThemedText style={styles.noteAuthor}>{note.author_name}</ThemedText>
                      <ThemedText style={styles.noteTime}>{getRelativeTime(note.created_at)}</ThemedText>
                    </View>
                    <ThemedText style={styles.noteMessage}>{note.message}</ThemedText>
                    {/* Position badges */}
                    {note.position_ids && note.position_ids.length > 0 && (
                      <View style={styles.notePositions}>
                        {note.position_ids.length === positions.length && positions.length > 0 ? (
                          <Chip text="All Positions" style={styles.positionBadge} />
                        ) : (
                          note.position_ids.map((posId) => {
                            const pos = positions.find((p) => p.id === posId);
                            return <Chip key={posId} text={pos?.name || posId} style={styles.positionBadge} />;
                          })
                        )}
                      </View>
                    )}
                  </View>
                  <Pressable style={styles.removeButton} onPress={() => handleRemoveNote(note.id)}>
                    <MaterialCommunityIcons name="close-circle" size={20} color={colors.textSecondary} />
                  </Pressable>
                </View>
              ))}

              {/* Inherited notes (read-only) */}
              {inheritedNotes.map((note) => (
                <View key={note.id} style={[styles.noteItem, styles.noteItemInherited]}>
                  <View style={styles.noteContent}>
                    <View style={styles.noteHeader}>
                      <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                        <ThemedText style={styles.noteAuthor}>{note.author_name}</ThemedText>
                        <Chip text="From Project" style={styles.inheritedBadge} />
                      </View>
                      <ThemedText style={styles.noteTime}>{getRelativeTime(note.created_at)}</ThemedText>
                    </View>
                    <ThemedText style={styles.noteMessage}>{note.message}</ThemedText>
                    {/* Position badges */}
                    {note.position_ids && note.position_ids.length > 0 && (
                      <View style={styles.notePositions}>
                        {note.position_ids.length === positions.length && positions.length > 0 ? (
                          <Chip text="All Positions" style={styles.positionBadge} />
                        ) : (
                          note.position_ids.map((posId) => {
                            const pos = positions.find((p) => p.id === posId);
                            return <Chip key={posId} text={pos?.name || posId} style={styles.positionBadge} />;
                          })
                        )}
                      </View>
                    )}
                  </View>
                  {/* No remove button for inherited notes */}
                </View>
              ))}
            </>
          )}
        </ScrollView>
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <ThemedText style={styles.noteCount}>
          {localNotes.length} note{localNotes.length !== 1 ? "s" : ""} on this entity
          {inheritedNotes.length > 0 && `, ${inheritedNotes.length} inherited`}
        </ThemedText>
        <View style={styles.footerButtons}>
          {onCancel && (
            <Button
              text="Cancel"
              style={[styles.cancelButton, { borderColor: colors.border }]}
              textStyle={{ color: colors.text }}
              onPress={onCancel}
            />
          )}
          <Button text="Save" style={styles.confirmButton} onPress={handleConfirm} />
        </View>
      </View>
    </View>
  );
}
